import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import PaidPlanModal from "@/components/PaidPlanModal";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { BookOpen, Heart, FlaskConical, Flame, Lightbulb } from "lucide-react";

const SUBJECT_BG = { Anatomy: "bg-blue-500", Physiology: "bg-rose-500", Biochemistry: "bg-purple-500" };
const SUBJECT_LETTER = { Anatomy: "A", Physiology: "P", Biochemistry: "B" };
const SUBJECT_DESCS = {
  Anatomy:      "Gross anatomy, histology, embryology and clinical correlations for Semester 1 practice.",
  Physiology:   "Organ systems, homeostasis, clinical physiology and applied concepts for exam-style practice.",
  Biochemistry: "Metabolism, enzymes, molecular biology and clinical biochemistry questions for Semester 1.",
};

const SORT_TABS = [
  { key: "recommended", label: "Recommended order" },
  { key: "live",        label: "All live subjects" },
  { key: "unanswered",  label: "Most unanswered first" },
];

export default function PracticeSelectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subjects,    setSubjects]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [sortTab,     setSortTab]     = useState("recommended");

  useEffect(() => {
    axios.get(`${API}/subjects`)
      .then(r => setSubjects(r.data))
      .catch(() => toast.error("Failed to load subjects"))
      .finally(() => setLoading(false));
  }, []);

  const handleSubjectClick = (subject) => {
    if (subject.is_live) navigate(`/practice/${subject.name}`);
    else toast.info("Coming soon! This subject is being prepared.");
  };

  const recommendedSubject = useMemo(() => {
    const live = subjects.filter(s => s.is_live);
    if (!live.length) return null;
    return live.slice().sort(
      (a, b) => ((b.question_count || 0) - (b.attempted_count || 0)) - ((a.question_count || 0) - (a.attempted_count || 0))
    )[0];
  }, [subjects]);

  const sortedSubjects = useMemo(() => {
    const list = [...subjects];
    if (sortTab === "live") return list.sort((a, b) => (b.is_live ? 1 : 0) - (a.is_live ? 1 : 0));
    if (sortTab === "unanswered") return list.sort((a, b) =>
      ((b.question_count || 0) - (b.attempted_count || 0)) - ((a.question_count || 0) - (a.attempted_count || 0))
    );
    return list.sort((a, b) => {
      if (a.is_live !== b.is_live) return a.is_live ? -1 : 1;
      return ((b.question_count || 0) - (b.attempted_count || 0)) - ((a.question_count || 0) - (a.attempted_count || 0));
    });
  }, [subjects, sortTab]);

  const recUnans = recommendedSubject
    ? Math.max(0, (recommendedSubject.question_count || 0) - (recommendedSubject.attempted_count || 0))
    : 0;

  // Free plan counts from user object
  const saUsed = Math.max(0, 4 - (user?.monthly_short_answers_remaining ?? 4));
  const seUsed = Math.max(0, 2 - (user?.monthly_short_essays_remaining  ?? 2));
  const leUsed = Math.max(0, 1 - (user?.monthly_long_essays_remaining   ?? 1));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation role="student" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">

          {/* Header */}
          <div className="mb-2">
            <h1 className="text-2xl lg:text-3xl font-heading font-bold text-[#2E3A59] mb-1">Select a Subject</h1>
            <p className="text-slate-500 text-sm">RGUHS Semester {user?.semester || 1} · Pick the subject you want to practise today</p>
          </div>

          {/* Monthly quota strip */}
          <div className="flex items-center gap-4 flex-wrap mb-5 mt-3">
            {[
              { label: `${4 - saUsed}/4 short answers`, used: saUsed >= 4 },
              { label: `${2 - seUsed}/2 short essays`,  used: seUsed >= 2 },
              { label: `${1 - leUsed}/1 long essays`,   used: leUsed >= 1 },
            ].map(({ label, used }) => (
              <span key={label} className={`text-xs font-semibold px-3 py-1 rounded-full ${used ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}>
                {label}
              </span>
            ))}
            <span className="text-xs text-slate-400">resets in 30 days</span>
            <button onClick={() => setUpgradeOpen(true)} className="text-xs font-bold text-[#2E3A59] underline underline-offset-2 hover:text-[#C9A227]">
              Upgrade →
            </button>
          </div>

          {/* Recommendation banner */}
          {!loading && recommendedSubject && (
            <div className="bg-[#1A2236] rounded-2xl p-5 lg:p-6 mb-6 text-white" data-testid="recommendation-banner">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold mb-1">Start with {recommendedSubject.name} today</h2>
                  <p className="text-slate-300 text-sm mb-3">
                    It has the most unanswered questions right now, so it is the fastest way to build momentum and improve your practice score.
                  </p>
                  {recUnans > 0 && (
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full border border-white/20 mb-4">
                      <span className="text-xs font-medium">
                        Recommended next step: {recommendedSubject.name} · {recUnans} unanswered questions
                      </span>
                    </div>
                  )}
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => navigate(`/practice/${recommendedSubject.name}`)}
                      className="px-5 py-2 bg-white text-[#2E3A59] rounded-lg font-semibold text-sm hover:bg-slate-100 transition-colors"
                      data-testid="continue-recommended-btn"
                    >
                      Continue with {recommendedSubject.name}
                    </button>
                    <button
                      onClick={() => document.getElementById("available-subjects")?.scrollIntoView({ behavior: "smooth" })}
                      className="px-5 py-2 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-sm hover:bg-white/20 transition-colors"
                      data-testid="see-all-subjects-btn"
                    >
                      See all subjects
                    </button>
                  </div>
                </div>

                {/* Right info boxes */}
                <div className="flex flex-col gap-2 lg:w-52 flex-shrink-0">
                  <div className="bg-white/10 rounded-xl p-3 border border-white/10">
                    <p className="text-xs text-slate-400 mb-1">Best subject to start</p>
                    <p className="text-sm font-bold text-white">{recommendedSubject.name}</p>
                    {recUnans > 0 && <p className="text-2xl font-bold text-white mt-0.5">{recUnans}</p>}
                    <p className="text-xs text-slate-400">Unanswered today</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-3 border border-white/10">
                    <p className="text-xs text-slate-400 mb-1">Free plan note</p>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      You have already used this month's essay attempts. Short answers are your best next option.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Available Subjects + sort tabs */}
          <div id="available-subjects" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-bold text-[#2E3A59]">Available Subjects</h2>
              <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                {SORT_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setSortTab(tab.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                      sortTab === tab.key ? "bg-white text-[#2E3A59] shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`sort-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-xs text-slate-400 hidden sm:block">Choose one to begin</span>
          </div>

          {/* Subject cards — 3-column grid matching mockup */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#2E3A59] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {sortedSubjects.map((subject) => {
                const bg   = SUBJECT_BG[subject.name]     || "bg-slate-500";
                const lt   = SUBJECT_LETTER[subject.name] || subject.name[0];
                const desc = SUBJECT_DESCS[subject.name]  || "";
                const isRec = recommendedSubject?.name === subject.name;

                return (
                  <button
                    key={subject.name}
                    onClick={() => handleSubjectClick(subject)}
                    disabled={!subject.is_live}
                    className={`text-left bg-white rounded-2xl border p-4 transition-all group relative ${
                      subject.is_live
                        ? "border-slate-200 hover:border-[#2E3A59] hover:shadow-md cursor-pointer"
                        : "border-slate-100 opacity-60 cursor-not-allowed"
                    }`}
                    data-testid={`subject-card-${subject.name.toLowerCase()}`}
                  >
                    {/* Best place to start badge */}
                    {isRec && subject.is_live && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                        <span className="text-xs font-bold px-2.5 py-0.5 bg-[#C9A227] text-white rounded-full shadow-sm whitespace-nowrap">
                          Best place to start
                        </span>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-3 mt-1">
                      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                        <span className="text-white font-bold text-base">{lt}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {subject.hot_questions > 0 && (
                          <span className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                            <Flame className="w-2.5 h-2.5" />{subject.hot_questions} hot
                          </span>
                        )}
                        {subject.is_live
                          ? <span className="text-xs font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Live</span>
                          : <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">Soon</span>
                        }
                      </div>
                    </div>

                    <h3 className="font-bold text-[#2E3A59] text-base mb-1">{subject.name}</h3>
                    <p className="text-xs text-slate-500 mb-3 line-clamp-2">{desc}</p>

                    <div className="flex items-center justify-between text-xs text-slate-400 mb-3">
                      <span>{subject.question_count || 0} questions</span>
                      <span>{subject.topic_count || 0} topics</span>
                    </div>

                    {subject.is_live && (
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <span className="text-xs font-semibold text-[#2E3A59] group-hover:text-[#C9A227] transition-colors">Start Practice</span>
                        <span className="text-slate-400">–</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Free plan tip */}
          <div className="p-4 bg-[#FDF8E6] rounded-xl border border-[#C9A227]/20">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#A6851D] mb-1">Free every 30 days, per course</p>
                <ul className="text-sm text-[#A6851D] space-y-1 mb-2">
                  <li>4 short answers · 2 short essays · 1 long essay</li>
                  <li className="text-[#A6851D]/60">Up to 50 MCQs — coming soon</li>
                </ul>
                <button onClick={() => setUpgradeOpen(true)} className="text-sm font-semibold text-[#2E3A59] underline underline-offset-2 hover:text-[#C9A227]">
                  See paid plan →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <PaidPlanModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}